import Check from "@mui/icons-material/Check";
import HighlightOff from "@mui/icons-material/HighlightOff";
import { Box, Button, ChipProps, Typography, styled } from "@mui/material";
import _ from "lodash";
import { useConfirm } from "material-ui-confirm";
import { ReactElement, useMemo, useState } from "react";
import { FormattedMessage } from "react-intl";

import { DropdownTagChip } from "~frontend/components/DropdownTagChip";
import { FlexBox } from "~frontend/components/FlexBox";
import TagChip from "~frontend/components/TagChip";
import { useIntl } from "~frontend/providers/IntlProvider";
import theme, { palette } from "~frontend/theme";
import formatTagName from "~frontend/util/formatTagName";

import { ActiveCampaignContact, CampaignForTexter } from "./types";

type TagOption = {
  type: "Tag";
  id: string;
  name: string;
};
type TagGroupOption = {
  type: "Group";
  id: string;
  name: string;
  children: { id: string; name: string }[];
};
type TagOrGroupOption = TagOption | TagGroupOption;

type AvailableTagOption = TagOption & { adding: boolean };
type AvailableTagGroupOption = TagGroupOption & {
  adding: { id: string; name: string } | false;
};
type AvailableTagOrGroupOption = AvailableTagOption | AvailableTagGroupOption;

type SavedTag = {
  id: string;
  name: string;
  removing: "implicit" | "explicit" | false;
};

const TagSectionHeader = styled(Box)({
  fontSize: "14px",
  fontWeight: 700,
  marginBottom: theme.spacing(1),
  height: "32px",
});

const TagSection = styled(Box)({
  marginBottom: theme.spacing(5),
});

const SavedTag = (props: ChipProps): ReactElement => {
  const [hover, setHover] = useState(false);
  return (
    <TagChip
      onMouseOver={() => setHover(true)}
      onMouseOut={() => setHover(false)}
      sx={{
        backgroundColor: palette.darkGreen,
        color: "white",
        fontWeight: 600,
        margin: "2px",
        border: "2px solid transparent",
        "&:hover": {
          backgroundColor: palette.darkGreen,
        },
      }}
      icon={
        hover ? (
          <HighlightOff
            sx={{
              width: "16px",
              height: "14px",
              paddingRight: "2px",
              fill: "white",
            }}
          />
        ) : (
          <Check
            sx={{
              width: "16px",
              height: "14px",
              paddingRight: "2px",
              fill: "white",
            }}
          />
        )
      }
      {...props}
    />
  );
};

const RemovedSavedTag = (props: ChipProps): ReactElement => (
  <TagChip
    sx={{
      color: palette.orange,
      backgroundColor: palette.white,
      fontWeight: 600,
      margin: "2px",
      border: "2px solid",
    }}
    icon={
      <HighlightOff
        sx={{
          width: "16px",
          height: "14px",
          paddingRight: "2px",
          fill: palette.orange,
        }}
      />
    }
    {...props}
  />
);

const SelectedTag = (props: ChipProps): ReactElement => (
  <TagChip
    sx={{
      background: "none",
      border: `2px solid ${palette.darkGreen}`,
      color: palette.darkGreen,
      fontWeight: 600,
      margin: "2px",
    }}
    icon={
      <Check
        sx={{
          width: "16px",
          height: "14px",
          paddingRight: "2px",
        }}
      />
    }
    {...props}
  />
);

const AvailableTag = ({
  tag,
  addUnsavedTag,
  removeUnsavedTag,
}: {
  tag: AvailableTagOrGroupOption;
  addUnsavedTag: (
    tag: AvailableTagOption | AvailableTagGroupOption["children"][number],
    tagGroup: AvailableTagGroupOption | null
  ) => void;
  removeUnsavedTag: (tagId: string) => void;
}): ReactElement => {
  if (tag.type === "Tag") {
    if (tag.adding) {
      return (
        <SelectedTag
          key={tag.id}
          label={tag.name}
          onClick={() => {
            removeUnsavedTag(tag.id);
          }}
        />
      );
    } else {
      return (
        <TagChip
          sx={{ margin: "2px", border: "2px solid transparent" }}
          key={tag.id}
          label={tag.name}
          onClick={() => {
            addUnsavedTag(tag, null);
          }}
        />
      );
    }
  }

  // Groups
  if (tag.adding) {
    const tagId = tag.adding.id;
    return (
      <SelectedTag
        key={tag.id}
        label={`${tag.name} > ${tag.adding.name}`}
        onClick={() => {
          removeUnsavedTag(tagId);
        }}
      />
    );
  }

  return (
    <DropdownTagChip
      key={tag.id}
      label={tag.name}
      menuItems={tag.children.map(child => ({
        key: child.id,
        label: child.name,
        onClick: () => {
          addUnsavedTag(child, tag);
        },
      }))}
    />
  );
};

export default function Tags({
  contactTags,
  campaign,
  unsavedTags,
  removedSavedTagIds,
  addUnsavedTag,
  removeUnsavedTag,
  removeSavedTag,
  cancelRemoveSavedTag,
  mutationPending,
  onSaveTags,
}: {
  contactTags?: ActiveCampaignContact["tags"];
  campaign?: CampaignForTexter;
  unsavedTags: { tagId: string; tagGroupId: string | null }[];
  removedSavedTagIds: string[];
  addUnsavedTag: (tagId: string, tagGroupId: string | null) => void;
  removeUnsavedTag: (tagId: string) => void;
  removeSavedTag: (tagId: string) => void;
  cancelRemoveSavedTag: (tagId: string) => void;
  mutationPending: boolean;
  onSaveTags: () => void;
}): ReactElement {
  const allTags: TagOrGroupOption[] = useMemo(() => {
    // We are going to build up a map of all tags and groups by their IDs
    const tagsAndGroupsById: Record<string, TagOrGroupOption> = {};

    // Add the manual tags & tag groups first
    if (campaign?.tags) {
      campaign.tags.forEach(tag => {
        tagsAndGroupsById[tag.id] = {
          type: "Tag",
          id: tag.id,
          name: tag.name,
        };
      });
    }

    if (campaign?.tagGroups) {
      campaign.tagGroups.forEach(group => {
        tagsAndGroupsById[group.id] = {
          type: "Group",
          id: group.id,
          name: group.name,
          children: group.tags.map(tag => ({ id: tag.id, name: tag.name })),
        };
      });
    }

    // Then, go through the response script tags to add any missing tags/groups
    if (campaign?.responseScriptGroups) {
      campaign.responseScriptGroups.forEach(group => {
        group.memberships.forEach(membership => {
          membership.script.tags.forEach(tag => {
            if (tag.group) {
              const groupEntry = tagsAndGroupsById[tag.group.id];
              if (groupEntry && groupEntry.type === "Group") {
                // Group already exists -- add the tag to its children if not already present
                if (!groupEntry.children.find(child => child.id === tag.id)) {
                  groupEntry.children.push({ id: tag.id, name: tag.name });
                }
              } else {
                // No group yet -- create it with a single child
                tagsAndGroupsById[tag.group.id] = {
                  type: "Group",
                  id: tag.group.id,
                  name: tag.group.name,
                  children: [{ id: tag.id, name: tag.name }],
                };
              }
            } else {
              if (!tagsAndGroupsById[tag.id]) {
                tagsAndGroupsById[tag.id] = {
                  type: "Tag",
                  id: tag.id,
                  name: tag.name,
                };
              }
            }
          });
        });
      });
    }

    // Now, construct a flat list of all tags. As we do so, we'll check
    // for tags / group options that are being added
    return _.sortBy(
      Object.values(tagsAndGroupsById).map(option => {
        if (option.type === "Tag") {
          return option;
        } else {
          return {
            ...option,
            children: _.sortBy(option.children, "name"),
          };
        }
      }),
      "name"
    );
  }, [campaign?.tags, campaign?.tagGroups, campaign?.responseScriptGroups]);

  // Transform our TagOrGroupOption[] into AvailableTagOrGroupOption[], marking
  // which tags are being added and removing those that are already saved.
  //
  // We do this in a separate useMemo so that we don't have to recompute
  // allTag (which only changes when the campaign is edited) when the
  // ephemeral unsavedTags change.
  const availableTags: AvailableTagOrGroupOption[] = useMemo(() => {
    const unsavedTagIdsSet = new Set(unsavedTags.map(t => t.tagId));
    const savedTagIdsSet = new Set(contactTags?.map(ct => ct.tag.id) || []);

    const options: AvailableTagOrGroupOption[] = [];

    allTags.forEach(option => {
      if (option.type === "Tag") {
        if (!savedTagIdsSet.has(option.id)) {
          options.push({
            ...option,
            adding: unsavedTagIdsSet.has(option.id),
          });
        }
      } else {
        const availableChildren = option.children.filter(
          child => !savedTagIdsSet.has(child.id)
        );
        if (availableChildren.length > 0) {
          const addingChild = availableChildren.find(child =>
            unsavedTagIdsSet.has(child.id)
          );
          options.push({
            ...option,
            children: availableChildren,
            adding: addingChild || false,
          });
        }
      }
    });

    return options;
  }, [allTags, unsavedTags, contactTags]);

  // Construct our saved tags list. This augments the list of tags applied
  // to the contact with the current state of whether they are being removed,
  // and whether that's an implicit removal (due to being replaced by an
  // unsaved tag in the same group) or an explicit removal (due to user action).
  const savedTags: SavedTag[] = (contactTags || []).map(contactTag => {
    if (!contactTag.tag.group) {
      // No group -- no implicit removals possible
      return {
        id: contactTag.tag.id,
        name: contactTag.tag.name,
        removing: removedSavedTagIds.includes(contactTag.tag.id)
          ? "explicit"
          : false,
      };
    }

    const contactTagGroupId = contactTag.tag.group.id;

    // Check for implicit removal due to an unsaved tag in the same group
    const implicitRemoval = unsavedTags.some(
      unsavedTag => unsavedTag.tagGroupId === contactTagGroupId
    );

    return {
      id: contactTag.tag.id,
      name: formatTagName(contactTag.tag),
      removing: implicitRemoval
        ? "implicit"
        : removedSavedTagIds.includes(contactTag.tag.id)
          ? "explicit"
          : false,
    };
  });

  const confirm = useConfirm();
  const intl = useIntl();

  const handleAddTag = async (
    tag: AvailableTagOption | AvailableTagGroupOption["children"][number],
    tagGroup: AvailableTagGroupOption | null
  ) => {
    if (tagGroup) {
      const oldTag = contactTags?.find(
        t => t.tag.group?.id === tagGroup.id
      )?.tag;
      if (oldTag) {
        // We're adding a tag in a group that already has a saved tag. Warn the
        // user that this will implicitly remove the existing saved tag.
        const { confirmed } = await confirm({
          title: intl.formatMessage({
            defaultMessage: "Warning",
            description: "Title for warning modal about removing existing tag",
          }),
          description: intl.formatMessage(
            {
              defaultMessage:
                'Selecting "{newTagName}" will overwrite the previous selection of "{oldTagName}".',
              description:
                "Body text for warning modal about removing existing tag",
            },
            {
              newTagName: formatTagName({ name: tag.name, group: tagGroup }),
              oldTagName: formatTagName(oldTag),
            }
          ),
          cancellationText: intl.formatMessage({
            defaultMessage: "Cancel",
            description: "Cancel button",
          }),
          confirmationText: intl.formatMessage({
            defaultMessage: "Overwrite Tag",
            description:
              "Button to confirm overwriting an existing tag with a new tag",
          }),
        });
        if (!confirmed) {
          return;
        }
      }
    }

    addUnsavedTag(tag.id, tagGroup?.id ?? null);
  };

  return (
    <Box>
      <TagSectionHeader>
        <FlexBox justifyContent="space-between" alignItems="center">
          <Box sx={{ flex: 1 }}>
            <FormattedMessage
              defaultMessage="Saved Tags"
              description="Header for section displaying tags applied to a contact"
            />
          </Box>
          <Button
            sx={{
              visibility:
                unsavedTags.length > 0 || removedSavedTagIds.length > 0
                  ? undefined
                  : "hidden",
            }}
            size="small"
            color="primary"
            variant="contained"
            onClick={onSaveTags}
            disabled={mutationPending}
          >
            <FormattedMessage
              defaultMessage="Save Tags"
              description="Button text to save tags for a contact"
            />
          </Button>
        </FlexBox>
      </TagSectionHeader>
      <TagSection>
        {savedTags.length === 0 ? (
          <Typography display="inline" sx={{ fontStyle: "italic" }}>
            <FormattedMessage
              defaultMessage="No saved tags"
              description="Message shown when there are no saved tags applied to a contact"
            />
          </Typography>
        ) : (
          <>
            {savedTags.map(tag =>
              tag.removing ? (
                <RemovedSavedTag
                  key={tag.id}
                  label={tag.name}
                  onClick={
                    tag.removing === "implicit"
                      ? undefined
                      : () => cancelRemoveSavedTag(tag.id)
                  }
                />
              ) : (
                <SavedTag
                  key={tag.id}
                  label={tag.name}
                  onClick={() => removeSavedTag(tag.id)}
                />
              )
            )}
          </>
        )}
      </TagSection>
      <TagSectionHeader>
        <FormattedMessage
          defaultMessage="Unsaved Tags"
          description="Header for section displaying tags that can be applied to a contact"
        />
      </TagSectionHeader>

      <TagSection>
        {availableTags.length === 0 ? (
          <Typography display="inline" sx={{ fontStyle: "italic" }}>
            <FormattedMessage
              defaultMessage="No available tags"
              description="Message shown when there are no tags that can be applied to a contact"
            />
          </Typography>
        ) : (
          <>
            {availableTags.map(tag => (
              <AvailableTag
                tag={tag}
                addUnsavedTag={handleAddTag}
                removeUnsavedTag={removeUnsavedTag}
              />
            ))}
          </>
        )}
      </TagSection>
    </Box>
  );
}
